import { IRL_FIELDS, type IRLFieldKey, type IRLStatus, type IRLPriority, type IRLRequest } from "./types";

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
}

export type Mapping = (IRLFieldKey | "")[];

/**
 * Pick the row most likely to be the real IRL header by trying each row
 * in `matrix.slice(0, 15)` and scoring by how many IRL fields its mapping
 * recognizes. A row that maps the required `request` field beats a row
 * that doesn't. Ties break to the earlier row.
 *
 * Falls back to "first row with >=2 non-empty cells" when no candidate
 * yields a usable mapping — same shape as the old auto-detect so existing
 * behavior is preserved on weird inputs.
 */
export function findBestHeaderRow(matrix: string[][]): number {
  const limit = Math.min(matrix.length, 15);
  let bestIdx = -1;
  let bestScore = -1;
  for (let i = 0; i < limit; i++) {
    const row = matrix[i] ?? [];
    const nonEmpty = row.filter((c) => (c ?? "").trim() !== "").length;
    if (nonEmpty < 2) continue;
    const mapping = suggestMapping(row);
    const mappedCount = mapping.filter((m) => !!m).length;
    const hasRequest = mapping.includes("request") ? 1 : 0;
    // request match is the strongest signal — weight it heavily so a row
    // that matches "Request" + 1 field beats a row that matches 3 random fields.
    const score = hasRequest * 100 + mappedCount;
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  if (bestIdx >= 0) return bestIdx;
  // Legacy fallback.
  for (let i = 0; i < limit; i++) {
    const row = matrix[i] ?? [];
    if (row.filter((c) => (c ?? "").trim() !== "").length >= 2) return i;
  }
  return 0;
}

export function suggestMapping(headers: string[]): Mapping {
  const used = new Set<IRLFieldKey>();
  return headers.map((header) => {
    const norm = normalize(header);
    if (!norm) return "";

    let best: { key: IRLFieldKey; score: number } | null = null;
    for (const field of IRL_FIELDS) {
      if (used.has(field.key)) continue;
      for (const hint of field.hints) {
        const h = normalize(hint);
        let score = 0;
        if (norm === h) score = 100;
        else if (norm.startsWith(h) || norm.endsWith(h)) score = 60;
        else if (norm.includes(h)) score = 40;
        else if (h.includes(norm) && norm.length >= 2) score = 25;
        if (score > 0 && (!best || score > best.score)) {
          best = { key: field.key, score };
        }
      }
    }

    if (best) {
      used.add(best.key);
      return best.key;
    }
    return "";
  });
}

function normalizeStatus(raw: string): IRLStatus {
  const s = raw.toLowerCase();
  if (!s) return "open";
  if (/(fulfilled|complete|done|closed|received|provided)/.test(s)) return "fulfilled";
  if (/(partial|in[\s-]?progress|wip|started)/.test(s)) return "partial";
  if (/(withdraw|cancel|n\/a|na|removed)/.test(s)) return "withdrawn";
  return "open";
}

/**
 * Convert any priority-ish string an uploader might use into our 3-bucket
 * model. Returns undefined for blanks so the row stays "no priority assigned"
 * rather than getting forced to a default.
 */
function normalizePriority(raw: string): IRLPriority | undefined {
  const s = raw.toLowerCase().trim();
  if (!s) return undefined;
  if (/^(h|hi|high|critical|urgent|p0|p1|1|red)/.test(s)) return "high";
  if (/^(l|lo|low|nice[- ]?to[- ]?have|p3|p4|3|4|green)/.test(s)) return "low";
  if (/^(m|med|medium|moderate|normal|p2|2|amber|yellow)/.test(s)) return "medium";
  return "medium";
}

/**
 * Convert sliced data rows into IRLRequest objects.
 *
 * `originalRowOffset` should be `headerRowIdx + 1` so each request remembers
 * its absolute matrix row index — needed by Export to update the right cell
 * in the original workbook without disturbing unmapped data.
 */
export function buildRequests(
  rows: string[][],
  mapping: Mapping,
  originalRowOffset = 0,
): IRLRequest[] {
  const fieldToCol: Partial<Record<IRLFieldKey, number>> = {};
  mapping.forEach((field, idx) => {
    if (field) fieldToCol[field] = idx;
  });

  const get = (row: string[], key: IRLFieldKey): string => {
    const col = fieldToCol[key];
    if (col === undefined) return "";
    return (row[col] ?? "").trim();
  };

  // Drop only TRULY blank rows (every mapped cell empty). A row with a
  // number but no request text — e.g., a section header or a row whose
  // request lives split across visual rows — must be KEPT so that:
  //   (a) the buyer's original numbering stays contiguous in the UI, and
  //   (b) on export the row round-trips to its original position instead of
  //       getting blanked by the deleted-row pass.
  return rows
    .map((row, idx): { row: string[]; idx: number; hasContent: boolean } => {
      const hasContent = mapping.some(
        (field, col) => !!field && (row[col] ?? "").trim() !== "",
      );
      return { row, idx, hasContent };
    })
    .filter((r) => r.hasContent)
    .map(({ row, idx }): IRLRequest => {
      const rawNumber = get(row, "number");
      const number = rawNumber || String(idx + 1);
      const topic = get(row, "topic");
      const entityName = get(row, "entityName");
      const advisor = get(row, "advisor");
      const request = get(row, "request");
      const rawStatus = get(row, "status");
      const rawPriority = get(row, "priority");
      const status = normalizeStatus(rawStatus);
      const priority = normalizePriority(rawPriority);

      // Capture the original raw cell text per mapped field. Export uses
      // this to write back what was there for fields the user didn't
      // edit — avoids overwriting "Done" with "Fulfilled" etc.
      const rawValues: Partial<Record<IRLFieldKey, string>> = {};
      if (fieldToCol.number !== undefined) rawValues.number = rawNumber;
      if (fieldToCol.topic !== undefined) rawValues.topic = topic;
      if (fieldToCol.entityName !== undefined) rawValues.entityName = entityName;
      if (fieldToCol.advisor !== undefined) rawValues.advisor = advisor;
      if (fieldToCol.request !== undefined) rawValues.request = request;
      if (fieldToCol.status !== undefined) rawValues.status = rawStatus;
      if (fieldToCol.priority !== undefined) rawValues.priority = rawPriority;

      return {
        id: crypto.randomUUID(),
        number,
        topic: topic || undefined,
        entityName: entityName || undefined,
        advisor: advisor || undefined,
        request,
        status,
        priority,
        originalRowIndex: originalRowOffset + idx,
        editedFields: [],
        rawValues,
      };
    });
}

export function isMappingValid(mapping: Mapping): boolean {
  return mapping.includes("request");
}
